import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Mic, MicOff, Volume2, VolumeX, Phone, Loader2 } from 'lucide-react';
import { Card } from './ui/card';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const cbTranslations = {
  de: {
    greeting: 'Hallo! 👋 Ich bin Ihr virtueller Berater bei Gravante. Ich helfe Ihnen gerne bei allen Fragen zu Solar, Wärmepumpen, Elektrik, Dacharbeiten und mehr! Sie können auch das Mikrofon nutzen, um mit mir zu sprechen.',
    speechNotSupported: 'Spracherkennung wird von Ihrem Browser nicht unterstützt.',
    chatError: 'Entschuldigung, es gab einen Fehler. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt unter +49 157 82283963.',
    q1: 'Was kostet eine Solaranlage?',
    q2: 'Elektroinstallation Preise?',
    q3: 'Welche Förderungen gibt es?',
    openChat: 'Chat öffnen',
    advisor: 'Berater',
    advisorName: 'Gravante Berater',
    online: 'Online',
    contact: 'Kontakt',
    voiceOn: 'Sprachausgabe an',
    voiceOff: 'Sprachausgabe aus',
    closeChat: 'Chat schließen',
    audioLoading: 'Audio wird geladen...',
    speaking: 'Spricht...',
    stopRecording: 'Aufnahme stoppen',
    speak: 'Sprechen',
    listening: '🎤 Ich höre zu...',
    inputPlaceholder: 'Schreiben oder sprechen...'
  },
  en: {
    greeting: 'Hello! 👋 I am your virtual advisor at Gravante. I am happy to help you with all questions about solar, heat pumps, electrics, roofing and more! You can also use the microphone to talk to me.',
    speechNotSupported: 'Speech recognition is not supported by your browser.',
    chatError: 'Sorry, there was an error. Please try again or contact us directly at +49 157 82283963.',
    q1: 'How much does a solar system cost?',
    q2: 'Electrical installation prices?',
    q3: 'What subsidies are available?',
    openChat: 'Open chat',
    advisor: 'Advisor',
    advisorName: 'Gravante Advisor',
    online: 'Online',
    contact: 'Contact',
    voiceOn: 'Voice output on',
    voiceOff: 'Voice output off',
    closeChat: 'Close chat',
    audioLoading: 'Loading audio...',
    speaking: 'Speaking...',
    stopRecording: 'Stop recording',
    speak: 'Speak',
    listening: '🎤 Listening...',
    inputPlaceholder: 'Type or speak...'
  }
};

const GlobalChatbot = () => {
  const { language } = useLanguage();
  const t = cbTranslations[language] || cbTranslations.de;
  const [isOpen, setIsOpen] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Prime the audio element during a user gesture so mobile browsers allow
  // programmatic playback of the AI voice later (avoids autoplay blocking).
  const unlockAudio = () => {
    try {
      if (!audioRef.current) audioRef.current = new Audio();
      if (audioUnlockedRef.current) return;
      const a = audioRef.current;
      a.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
      a.muted = true;
      const p = a.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          a.pause();
          a.currentTime = 0;
          a.muted = false;
          audioUnlockedRef.current = true;
        }).catch(() => { a.muted = false; });
      }
    } catch (e) { /* ignore */ }
  };

  // Initialize Speech Recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'de-DE';

        recognitionRef.current.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map(result => result[0].transcript)
            .join('');
          setInput(transcript);
          
          if (event.results[0].isFinal) {
            setTimeout(() => {
              setIsListening(false);
            }, 500);
          }
        };

        recognitionRef.current.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
        };

        recognitionRef.current.onend = () => {
          setIsListening(false);
        };
      }
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Keep speech recognition language in sync (no state updates here)
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = language === 'en' ? 'en-US' : 'de-DE';
    }
  }, [language]);

  // OpenAI TTS - Professional voice
  const speakText = async (text) => {
    if (!voiceEnabled) return;
    
    // Stop any ongoing audio
    stopSpeaking();
    setIsLoadingAudio(true);
    
    try {
      const response = await fetch(`${API_URL}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text,
          voice: 'nova' // Energetic, friendly voice
        })
      });
      
      if (!response.ok) {
        throw new Error('TTS failed');
      }
      
      const data = await response.json();

      // Reuse a single (already user-unlocked) audio element for reliable
      // playback of the professional AI voice, including on mobile.
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      const audio = audioRef.current;
      audio.muted = false;
      audio.src = `data:audio/mp3;base64,${data.audio_base64}`;

      audio.onplay = () => {
        setIsSpeaking(true);
        setIsLoadingAudio(false);
      };

      audio.onended = () => {
        setIsSpeaking(false);
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        setIsLoadingAudio(false);
      };

      await audio.play();

    } catch (error) {
      console.error('TTS error:', error);
      setIsLoadingAudio(false);
      setIsSpeaking(false);
      // No browser fallback: we only ever use the clear professional AI voice
      // (a robotic browser voice is worse than staying silent).
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
    setIsLoadingAudio(false);
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert(t.speechNotSupported);
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      unlockAudio();
      setInput('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    // Unlock audio playback within this user gesture (for mobile voice)
    unlockAudio();

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    stopSpeaking();

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          history: messages.slice(-10),
          language: language
        })
      });

      if (!response.ok) throw new Error('Chat failed');

      const data = await response.json();
      setSessionId(data.session_id);
      const assistantMessage = data.response;
      setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
      
      if (voiceEnabled) {
        speakText(assistantMessage);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = t.chatError;
      setMessages(prev => [...prev, { role: 'assistant', content: errorMessage }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isListening && input.trim() && messages.length > 0) {
      const timer = setTimeout(() => {
        if (input.trim()) {
          sendMessage();
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isListening]);

  const quickQuestions = [
    t.q1,
    t.q2,
    t.q3
  ];

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => { unlockAudio(); setIsOpen(true); }}
        data-testid="chatbot-open-btn"
        className={`fixed bottom-6 right-6 z-40 bg-gradient-to-r from-amber-500 to-amber-600 text-white p-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 ${isOpen ? 'hidden' : 'flex'} items-center gap-2`}
        aria-label={t.openChat}
      >
        <MessageCircle className="w-6 h-6" />
        <span className="hidden sm:inline font-medium">{t.advisor}</span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-full max-w-md" data-testid="chatbot-window">
          <Card className="shadow-2xl border-0 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-sky-600 to-sky-700 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-lg p-1">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/ocvvkw7r_IMG_0068.png"
                    alt="Gravante Logo"
                    className="h-8 w-auto"
                  />
                </div>
                <div>
                  <h3 className="text-white font-bold flex items-center gap-2">
                    {t.advisorName}
                    <Mic className="w-4 h-4 text-green-300" />
                  </h3>
                  <div className="flex items-center gap-1 text-green-300 text-xs">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    {t.online}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {showPhone ? (
                  <a
                    href="tel:+4915782283963"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 rounded-full text-white text-sm font-medium transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span>+49 157 82283963</span>
                  </a>
                ) : (
                  <button
                    onClick={() => setShowPhone(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm font-medium transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span>{t.contact}</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    if (isSpeaking) stopSpeaking();
                    setVoiceEnabled(!voiceEnabled);
                  }}
                  className={`p-2 rounded-full transition-colors ${voiceEnabled ? 'bg-white/20 text-white' : 'bg-white/10 text-white/50'}`}
                  title={voiceEnabled ? t.voiceOn : t.voiceOff}
                >
                  {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                </button>
                <button 
                  onClick={() => {
                    stopSpeaking();
                    setIsOpen(false);
                  }} 
                  className="text-white/80 hover:text-white p-1 hover:bg-white/10 rounded-full transition-colors"
                  aria-label={t.closeChat}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 bg-gray-50 space-y-4">
              {[{ role: 'assistant', content: t.greeting }, ...messages].map((msg, index) => (
                <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-sky-600 text-white rounded-br-none' 
                      : 'bg-white shadow-sm rounded-bl-none'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white shadow-sm rounded-2xl rounded-bl-none p-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </div>
              )}
              {(isSpeaking || isLoadingAudio) && (
                <div className="flex justify-center">
                  <div className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs flex items-center gap-2">
                    {isLoadingAudio ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {t.audioLoading}
                      </>
                    ) : (
                      <>
                        <Volume2 className="w-3 h-3 animate-pulse" />
                        {t.speaking}
                      </>
                    )}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Questions */}
            {messages.length === 0 && (
              <div className="px-4 py-2 bg-gray-100 flex gap-2 overflow-x-auto">
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(q)}
                    className="text-xs bg-white px-3 py-1.5 rounded-full border border-gray-200 hover:border-sky-500 hover:text-sky-600 whitespace-nowrap transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input with Voice */}
            <form onSubmit={sendMessage} className="p-4 bg-white border-t flex gap-2">
              <button
                type="button"
                onClick={toggleListening}
                disabled={loading}
                className={`p-2 rounded-full transition-all ${
                  isListening 
                    ? 'bg-red-500 text-white animate-pulse scale-110' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={isListening ? t.stopRecording : t.speak}
              >
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={isListening ? t.listening : t.inputPlaceholder}
                className={`flex-1 px-4 py-2 border rounded-full focus:outline-none focus:border-sky-500 ${isListening ? 'border-red-300 bg-red-50' : ''}`}
                disabled={loading || isListening}
              />
              <button
                type="submit"
                disabled={loading || !input.trim() || isListening}
                className="bg-sky-600 text-white p-2 rounded-full hover:bg-sky-700 disabled:opacity-50 transition-colors"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </Card>
        </div>
      )}
    </>
  );
};

export default GlobalChatbot;
